import { useMemo, useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, type Scheme, type StateRow } from "@/lib/api";
import { BenefitChip } from "@/lib/chips";

interface Props {
  onOpenScheme: (id: string) => void;
  onAskAi: (prompt: string) => void;
}

export function StatesPanel({ onOpenScheme, onAskAi }: Props) {
  const { data } = useApi(() => api.states(), []);
  const [active, setActive] = useState<string>("");
  const [schemes, setSchemes] = useState<Scheme[] | null>(null);

  const current: StateRow | undefined = useMemo(
    () => data?.items.find((s) => s.state === active),
    [data, active],
  );

  const select = async (name: string) => {
    setActive(name);
    setSchemes(null);
    try {
      const r = await api.search({ state: name, limit: 6 });
      setSchemes(r.items.filter((x) => x.source !== "ecosystem"));
    } catch (e) { console.error(e); }
  };

  return (
    <section id="states" className="max-w-6xl mx-auto px-5 py-20">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
        <div>
          <p className="section-eyebrow mb-2">chapter · four</p>
          <h2 className="section-title">
            State <span className="font-serif italic text-amber-300">ecosystems</span>.
          </h2>
          <p className="text-cream-100/60 mt-2 max-w-xl text-sm">
            Every state mission, incubator and accelerator — by state.
            Click a row to open the regional stack.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-[340px_1fr] gap-6 items-start">
        <div className="rounded-2xl border border-amber-400/20 bg-plum-900/40 p-3 max-h-[560px] overflow-y-auto backdrop-blur-sm">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/80 px-2 py-2 sticky top-0 bg-plum-900/70 backdrop-blur-sm z-10 rounded-t-lg">
            · {data?.items.length ?? 0} states on record ·
          </div>
          {data?.items.map((s, idx) => (
            <button key={s.state}
                    onClick={() => select(s.state)}
                    className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-left transition ${
                      active === s.state
                        ? "bg-amber-400/15 border border-amber-400/40"
                        : "border border-transparent hover:bg-white/[0.03]"
                    }`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="font-serif italic text-amber-300/55 text-xs w-5 shrink-0 text-right">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className={`inline-block w-1 h-5 rounded-sm ${
                  s.total >= 50 ? "bg-coral-500" : s.total >= 20 ? "bg-amber-400" : "bg-aqua-400"
                }`} />
                <span className="font-display text-sm text-cream-50 truncate">{s.state}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] shrink-0">
                {s.orgs > 0 && (
                  <span className="chip chip-emerald !text-[10px] !py-0 !px-1.5">{s.orgs}</span>
                )}
                <span className="font-mono text-cream-100/45">{s.total}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-amber-400/20 bg-plum-900/35 p-7 min-h-[560px]">
          {!current ? (
            <EmptyState />
          ) : (
            <>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300/80">
                    · postcard from ·
                  </div>
                  <h3 className="font-display text-[2.2rem] font-semibold mt-1 leading-[1.05] tracking-tight text-cream-50">
                    {current.state}
                  </h3>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <span className="chip chip-accent">{current.schemes} schemes</span>
                  <span className="chip chip-emerald">{current.orgs} orgs</span>
                  <span className="chip chip-saffron">{current.loans} loans</span>
                  <span className="chip">{current.subsidies} subsidies</span>
                </div>
              </div>

              {current.orgs_sample.length > 0 && (
                <>
                  <h4 className="mt-7 mb-3 font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300/80">
                    — mission, incubators, accelerators —
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {current.orgs_sample.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => onOpenScheme(o.id)}
                        className="group text-left border border-white/10 bg-plum-950/40 rounded-xl p-3.5 hover:border-amber-400/40 hover:bg-plum-950/70 transition"
                      >
                        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300/85">
                          {o.category || "Organisation"}
                        </div>
                        <div className="font-display text-sm font-semibold mt-1 text-cream-50 group-hover:text-amber-200 transition">
                          {o.name}
                        </div>
                        {o.application_url && (
                          <a
                            href={o.application_url}
                            target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[11px] text-cream-100/50 hover:text-amber-300 underline decoration-dotted underline-offset-2 mt-1.5 inline-block truncate max-w-full"
                          >
                            {o.application_url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                          </a>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <h4 className="mt-7 mb-3 font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300/80">
                — top schemes in {current.state} —
              </h4>
              <div className="space-y-2">
                {schemes === null ? (
                  <div className="text-[13px] text-cream-100/55 flex items-center gap-2">
                    <span className="loader" /> reading vārtā…
                  </div>
                ) : schemes.length === 0 ? (
                  <div className="text-[13px] text-cream-100/55">
                    No state-specific schemes filed — try central schemes.
                  </div>
                ) : (
                  schemes.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => onOpenScheme(s.id)}
                      className="group w-full text-left flex items-start gap-3 border border-white/8 bg-plum-950/35 hover:border-amber-400/35 hover:bg-plum-950/60 rounded-xl p-3.5 transition"
                    >
                      <div className="flex flex-col gap-1 shrink-0">
                        <BenefitChip kind={s.benefit_type} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display text-[14px] font-semibold text-cream-50 group-hover:text-amber-200 transition">
                          {s.name}
                        </div>
                        {s.summary && (
                          <div className="text-xs text-cream-100/55 mt-1 leading-snug">
                            {s.summary.length > 180 ? s.summary.slice(0, 180) + "…" : s.summary}
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>

              <button
                onClick={() =>
                  onAskAi(
                    `I want to build a company in ${current.state}. Give me the top state schemes, ` +
                      `incubators and central schemes I can combine. Include amounts and URLs.`,
                  )
                }
                className="mt-7 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl
                           bg-cream-50 hover:bg-amber-300 text-plum-950 font-display text-sm font-semibold tracking-wide transition"
              >
                <span className="font-serif italic">✦</span>
                Build in {current.state} — ask Ākāśavāṇī →
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="relative h-full flex flex-col items-center justify-center text-center py-20 px-6">
      <svg viewBox="0 0 100 130" className="w-16 h-20 mb-4 opacity-70" aria-hidden>
        <rect x="12" y="10" width="76" height="110" rx="3"
              fill="rgba(15,10,26,0.6)" stroke="rgba(243,193,116,0.45)" strokeWidth="1" />
        <rect x="20" y="20" width="60" height="2" fill="rgba(243,193,116,0.4)" />
        <rect x="20" y="28" width="40" height="1.5" fill="rgba(243,193,116,0.25)" />
        <rect x="20" y="40" width="55" height="1" fill="rgba(243,193,116,0.2)" />
        <rect x="20" y="46" width="48" height="1" fill="rgba(243,193,116,0.2)" />
        <rect x="20" y="52" width="52" height="1" fill="rgba(243,193,116,0.2)" />
        <circle cx="50" cy="85" r="12" fill="none" stroke="rgba(243,193,116,0.45)" strokeWidth="0.8" />
        <text x="50" y="89" textAnchor="middle"
              fontFamily="IBM Plex Serif" fontStyle="italic"
              fontSize="10" fill="rgba(243,193,116,0.85)">aff.</text>
        <text x="50" y="115" textAnchor="middle"
              fontFamily="IBM Plex Mono" fontSize="5"
              letterSpacing="1" fill="rgba(243,193,116,0.55)">CHOOSE A STATE</text>
      </svg>
      <p className="font-display text-lg text-cream-50">The postcard rack is full.</p>
      <p className="mt-1 text-[13px] text-cream-100/55 max-w-sm">
        Pick any state from the list — we&rsquo;ll pull its startup mission,
        the incubators worth knowing, and its live schemes.
      </p>
    </div>
  );
}
